/**
 * Mail derivations the FRONTEND owns.
 *
 * Nothing here talks to a mail provider — that is entirely behind the API (infra A.2-A.5).
 * These are the small display rules the screen needs: which folder is selected, what an
 * attachment can be used for, and how a sender is rendered when we have only their name.
 */

import type { MailAccount, MailAttachment, MailFolder, MailFolderSummary, MailMessage } from './types'

/** A folder selection is an account + a folder key; the shared folders have no account. */
export interface FolderSelection {
  folder: MailFolder
  account: MailAccount | null
}

export const sameFolder = (a: FolderSelection, b: FolderSelection): boolean =>
  a.folder === b.folder && a.account === b.account

export const selectionOf = (summary: MailFolderSummary): FolderSelection => ({
  folder: summary.key,
  account: summary.account,
})

/** The rail's group headings: each account's mailbox address, then the shared block. */
export const MAILBOX_ADDRESS: Record<MailAccount, string> = {
  customer: 'enquiries@bosun.app',
  supplier: 'procurement@bosun.app',
}

export const FOLDER_ICON: Record<MailFolder, string> = {
  inbox: '📥',
  flagged: '⚑',
  drafts: '📝',
  sent: '➤',
  deleted: '🗑️',
  archive: '🗄',
}

export interface FolderBadge {
  count: number
  /**
   * `true` when `count` is a stand-in total rather than a genuine count of the thing the badge
   * claims to show — currently only possible for Inbox. `MailFolderSummary` (`src/lib/types.ts`,
   * not this task's to extend) has no field for this, so it is recomputed here from the same
   * comparison `MailPage` used to decide whether it could afford to compute a real unread count
   * in the first place: only the OPEN folder's rows are actually loaded (`useMailbox`'s active-vs-
   * `limit=1` split — the fix for the eager 4x fetch), so Inbox's unread count is exact only while
   * Inbox is open; everywhere else `folder.unread` already equals `folder.total`.
   *
   * `FolderRail` renders `approx` badges visibly differently (a `~` prefix, dimmed, with a
   * tooltip) — a wrong-looking-right number ("47" read as "47 unread" when 3 are unread) is worse
   * than an honestly-labelled approximation, the same reasoning this file applies everywhere else
   * a field can't be trusted at face value.
   */
  approx: boolean
}

/**
 * What number (if any) the folder rail should badge a folder with. Returns `null` when there is
 * nothing worth showing (count is 0) — `FolderRail` renders no badge at all in that case, same as
 * before this existed.
 */
export function folderBadge(folder: MailFolderSummary, selected: FolderSelection): FolderBadge | null {
  /*
    The UNREAD count, for every folder — not `total` for the shared ones.

    `total` was the row count of the request that fetched them, and unopened folders are fetched
    with `limit: 1`, so Sent/Drafts/Deleted each badged a permanent "1" that described our own
    query rather than the mailbox. `unread` now comes from the server's `folder_counts`
    (`useMail.ts`), and is `undefined` when the server did not count that folder.

    Undefined and zero both render NO badge, for different reasons: nothing unread is not worth a
    chip, and a folder nobody counted must not be given a number.
  */
  const count = folder.unread
  if (count === undefined || count <= 0) return null
  // Still approximate for a folder that is not open: the count is the server's, taken when the
  // page was fetched, not a live figure.
  return { count, approx: selected.folder !== folder.key }
}

/** What the list header calls the current folder. */
export function folderTitle(selection: FolderSelection): string {
  if (selection.folder === 'inbox') {
    // `get_email_communications` (Q22, docs/bosun-frappe-open-questions.md) has no field that
    // says which mailbox a message belongs to, so `useMailFolders` now returns one flat,
    // `account: null` folder for the whole mailbox instead of faking a per-account split — see
    // its comment in `src/lib/api/useMail.ts`. The two named branches below are kept for any
    // caller that still passes a real account explicitly; `null` gets its own honest label
    // rather than silently falling into "Customer Enquiries".
    if (selection.account === 'supplier') return 'Supplier Mailbox'
    if (selection.account === 'customer') return 'Customer Enquiries'
    return 'All Mail'
  }
  return { flagged: 'Flagged', drafts: 'Drafts', sent: 'Sent', deleted: 'Deleted', archive: 'Archive' }[
    selection.folder
  ]
}

/**
 * What an EMPTY folder says. "No mail in this folder" is true of Inbox and Sent; for Drafts it
 * reads as "you have written none", when the truth is that the mailbox keeps no drafts at all
 * (`send_email` sends at once, and `get_emails` serves only Inbox and Sent — `useMail.ts`'s
 * `folderRows`). Deleted is derived from the archived rows of both, and says where they come from.
 */
export function emptyFolderNote(folder: MailFolder): string {
  if (folder === 'drafts') return 'The mailbox keeps no drafts — a message is sent the moment you press Send.'
  if (folder === 'deleted') return 'Nothing deleted yet — mail deleted from Inbox or Sent is listed here.'
  return 'No mail in this folder.'
}

/**
 * Only these can be parsed into requirement lines. A .docx or a signature image in a reply
 * has nothing to extract, and offering "Start RFQ" on one just produces a 422.
 */
const RFQ_ABLE: ReadonlySet<MailAttachment['kind']> = new Set(['xlsx', 'pdf', 'img'])

export const canStartRfq = (attachment: MailAttachment): boolean => RFQ_ABLE.has(attachment.kind)

export const ATTACHMENT_ICON: Record<MailAttachment['kind'], string> = {
  xlsx: '📊',
  pdf: '📕',
  doc: '📄',
  img: '🖼️',
  file: '📎',
}

/**
 * A stable colour per sender, so the same correspondent always looks the same. Hashing the
 * name keeps it deterministic without storing anything.
 */
const AVATAR_COLORS = ['#2563eb', '#0d9488', '#7c3aed', '#d97706', '#db2777', '#0891b2', '#4f46e5']

export function avatarColor(name: string): string {
  let hash = 0
  for (const ch of name) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0
  return AVATAR_COLORS[hash % AVATAR_COLORS.length]
}

/**
 * Which folders offer the extraction controls. Only what actually arrived from outside can
 * become a requirement — a Sent item, a Draft or something in Deleted cannot, and offering
 * "Upload file → RFQ" there is just noise. (Testing `mail.account` instead is always true:
 * every message has one.)
 */
export const isInboundFolder = (folder: MailFolder): boolean => folder === 'inbox' || folder === 'flagged'

/**
 * Supplier replies are parsed as quotations, not requirements — that is the Supplier
 * Quotations screen. The prototype makes the same split: "Build RFQ" on customer mail,
 * "Parse quote" on supplier mail.
 */
export const isCustomerMail = (mail: MailMessage): boolean => mail.account === 'customer'

/* ---------- "addressed to" filter ---------- */

/**
 * One address that appears somewhere in the mailbox's `to`/`cc`, with how many messages it is on.
 *
 * This backs the message list's "Addressed to" control: a purchaser who only wants the mail they
 * were copied on picks their address and the list narrows. It is a **view preference, not a
 * permission** — see `involvesAddress` below.
 */
export interface MailParticipant {
  address: string
  count: number
}

/**
 * Is `address` on this message's To or Cc line? Case-insensitive; `from` is deliberately NOT
 * matched — the question the control asks is "was I sent this?", not "did I touch it?".
 *
 * **This is convenience filtering, not access control.** Anything the filter hides is still in
 * the response the browser already received, so a client-side filter cannot be a permission
 * boundary. If purchasers must genuinely be unable to *see* other people's mail, the server has
 * to scope `get_email_communications` per user — raised as Q24 in
 * `docs/bosun-frappe-open-questions.md`.
 */
export function involvesAddress(mail: MailMessage, address: string): boolean {
  const needle = address.trim().toLowerCase()
  if (needle === '') return true
  return mail.to.concat(mail.cc).some((candidate) => candidate.trim().toLowerCase() === needle)
}

/**
 * Every distinct To/Cc address across the mailbox, busiest first, then alphabetically so the
 * order is stable between renders when counts tie.
 *
 * Derived from the loaded mail rather than from a user identity on purpose. Bosun has no real
 * address for the signed-in user: `ROLE_USERS` in `components/SessionProvider.tsx` holds display
 * names ("Akhil"), and `MAILBOX_ADDRESS` above is a pair of placeholders invented before Q22
 * established that nothing on the real server distinguishes the two mailboxes. Matching a name
 * against an email field, or filtering on an invented address, would silently return nothing.
 * Offering the addresses that are demonstrably present cannot do that.
 *
 * The real source eventually is `frappe.auth.get_logged_user` (contract §2), which returns the
 * session user's email — once the app authenticates with a session rather than a dev token, that
 * becomes the default selection instead of "Everyone".
 */
export function mailParticipants(messages: MailMessage[]): MailParticipant[] {
  const counts = new Map<string, { address: string; count: number }>()
  for (const message of messages) {
    // A message that lists the same address on both To and Cc still counts once.
    const seen = new Set<string>()
    for (const raw of message.to.concat(message.cc)) {
      const address = raw.trim()
      if (address === '') continue
      const key = address.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)
      const entry = counts.get(key)
      if (entry) entry.count += 1
      else counts.set(key, { address, count: 1 })
    }
  }
  return [...counts.values()].sort((a, b) => b.count - a.count || a.address.localeCompare(b.address))
}

/** Plain-text preview of an HTML body, for the list row when the API sends no preview. */
export function plainPreview(html: string, length = 64): string {
  return htmlToText(html).replace(/\s+/g, ' ').trim().slice(0, length)
}

/* ---------- forwarded-mail sender ---------- */

/** A sender identity pulled out of a quoted `From:` header, or `mail.from`/`mail.from_name`. */
export interface ForwardedSender {
  name: string
  email: string
}

/**
 * Gmail's forward banner: `---------- Forwarded message ---------`. Dash count is not exact
 * (10 left / 9 right in the live samples) so this only pins the words, not a fixed count — but it
 * is Gmail's marker specifically. Outlook's `-----Original Message-----` is a different product
 * with a different marker; nothing in this mailbox uses it, so there is nothing to verify a
 * pattern against, and none is added (task instruction: don't invent handling for variants you
 * can't verify).
 */
const FORWARD_MARKER = /-{3,}\s*Forwarded message\s*-{3,}/i

/**
 * Immediately after the marker, Gmail lays out `From:`/`Date:`/`Subject:`/`To:`/`Cc:` one per
 * line before the quoted body starts. Matched together so `meaningfulPreview` below can strip the
 * whole block in one pass, not just the `From:` line `parseForwardedSender` reads.
 */
const FORWARDED_HEADER_BLOCK = new RegExp(
  `${FORWARD_MARKER.source}\\s*\\n(?:(?:From|Date|Subject|To|Cc):[^\\n]*\\n?)*`,
  'i',
)

const FROM_LINE = /^From:\s*(.*)$/m

/**
 * `Name <email>`, `<email>` (Gmail renders just the bracket when there is no display name — the
 * live mailbox's `bipublisher-report@oracle.com` and `purchase@io-marine.com` rows), or a bare
 * `email` with no brackets. All three are real shapes seen on `rfqdev.etoserp.com`, 2026-08.
 */
function parseAddressLine(line: string): ForwardedSender | null {
  const bracketed = /^(.*?)<\s*([^<>\s]+@[^<>\s]+)\s*>\s*$/.exec(line)
  if (bracketed) {
    const email = bracketed[2].trim()
    const name = bracketed[1].trim()
    // No display name — Gmail's own rendering when the sender's account has none set (e.g. the
    // live `siraj@nosksa.com` row, whose name string IS the address, not a person's name).
    return { name: name || email, email }
  }
  if (/^[^\s<>@]+@[^\s<>]+$/.test(line)) return { name: line, email: line }
  return null
}

/**
 * Every message in this mailbox arrives forwarded (Gmail's "Forwarded message" banner) by the
 * same internal address, so `mail.from`/`mail.from_name` are never the actual customer — they're
 * whoever hit Forward. The customer is one line into the quoted header: `From: NAME <email>`.
 * This pulls that out.
 *
 * Runs on `htmlToText`'s output, not raw HTML — Gmail's forward banner turns into plain
 * `NAME <email>` text once the markup is stripped and entities decoded, which is far simpler and
 * more robust to match than the source HTML (`<strong class="gmail_sendername">…` wrapped in a
 * `<span>` wrapped mailto `<a>`, verified across the live mailbox's rows and not identical
 * between them — see `mail.test.ts`).
 *
 * Only trusts a `From:` line that is the very next line after the marker (blank lines aside) —
 * not just the first `From:` anywhere in the body. A forwarded RFQ can itself quote an earlier
 * "REMINDER" mail deeper in its own text (the live `sales6@aquamaristrading.com` row does), and
 * that inner quoting is not the customer this purchaser is triaging.
 *
 * Returns `null` — never a guess — when there is no forwarded header at all: this mailbox also
 * receives genuine, non-forwarded system mail (Google security alerts, a cPanel notice), and
 * callers must fall back to the real `from`/`from_name` rather than invent one.
 */
export function parseForwardedSender(bodyHtml: string): ForwardedSender | null {
  const text = htmlToText(bodyHtml)
  const marker = FORWARD_MARKER.exec(text)
  if (!marker) return null
  const after = text.slice(marker.index + marker[0].length)
  const fromLine = FROM_LINE.exec(after)
  if (!fromLine) return null
  if (after.slice(0, fromLine.index).trim() !== '') return null
  return parseAddressLine(fromLine[1].trim())
}

/**
 * The identity a purchaser should actually see for a row: the forwarded header's sender when one
 * parses, the real `from`/`from_name` otherwise. `forwarded` tells the caller which one it got,
 * for an optional "who forwarded this" hint — the forwarder's own name/address is never simply
 * discarded, just not the headline identity.
 */
export function displaySender(mail: Pick<MailMessage, 'body_html' | 'from' | 'from_name'>): {
  name: string
  email: string
  forwarded: boolean
} {
  const forwarded = parseForwardedSender(mail.body_html)
  if (forwarded) return { ...forwarded, forwarded: true }
  return { name: mail.from_name, email: mail.from, forwarded: false }
}

/**
 * A one-line preview that skips the forwarded header block ("Forwarded message" / From / Date /
 * Subject / To / Cc) when there is one, so the list row shows what the customer actually wrote
 * instead of the same boilerplate on every single row (`plainPreview(body_html)` on this mailbox
 * renders identically for all of it — the defect this exists to fix).
 *
 * Falls back to the whole body when there is no forwarded header to strip (non-forwarded system
 * mail), matching `plainPreview`'s behaviour for that case.
 */
export function meaningfulPreview(bodyHtml: string, length = 120): string {
  const text = htmlToText(bodyHtml).replace(FORWARDED_HEADER_BLOCK, '')
  return text.replace(/\s+/g, ' ').trim().slice(0, length)
}

const escapeHtml = (text: string): string =>
  // `&` first, or the entities written by the later replacements get double-escaped.
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

/**
 * Plain compose text → the `body_html` the contract carries.
 *
 * Escaping happens BEFORE the markup goes in. Doing it the other way escapes the `<br>` tags
 * this function just inserted, and the recipient reads a literal "<br>" at every line break.
 */
export function toBodyHtml(text: string): string {
  return text
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
    .join('')
}

/**
 * The inverse, for re-opening a draft and for rendering a received message: paragraph and line
 * breaks survive, and nothing is truncated. `plainPreview` is for a 64-character list row and
 * would quietly eat the user's text if used here.
 *
 * ---- Why the whitespace handling at the end is load-bearing ----
 *
 * A real received mail is not the tidy `<p>`-per-paragraph markup `toBodyHtml` produces. Frappe's
 * conversion of the AMS-RFQ-7603 message, for instance, contains runs like
 *
 *     <p>\t<br>\t<br>\t</p><p>\t<br>\t\t<br>\t\t\t<br> … \t\t\t\t\t\t\t\t\t\t</p>
 *
 * — the indentation of the ORIGINAL table-based HTML, preserved as literal tabs, with every source
 * line break turned into a `<br>`. Converted naively that becomes fifteen-odd blank lines, and
 * `.rtext` renders with `white-space: pre-wrap`, so the reader showed a screenful of emptiness
 * between "Request for Quotation" and "Shiby,". The text was never the problem; the source's own
 * indentation was.
 *
 * So: trailing horizontal whitespace is stripped per line (turning `\t`-only lines into empty
 * ones), then three-or-more consecutive newlines collapse to two. One blank line between
 * paragraphs is kept, because that IS meaningful. Nothing that carries content is touched.
 *
 * The draft round-trip is unaffected: `toBodyHtml` already splits on `\n{2,}`, so a run of blank
 * lines a user typed has collapsed to a single paragraph break before it ever reaches here.
 */
export function htmlToText(html: string): string {
  return (
    html
      // Stylesheet and script bodies are markup, not message text. HTML mail routinely carries a
      // <style> block, and dumping CSS into the reader is far worse than dropping it.
      .replace(/<(style|script)\b[^>]*>[\s\S]*?<\/\1>/gi, '')
      .replace(/<\/p>\s*/gi, '\n\n')
      .replace(/<br\s*\/?>/gi, '\n')
      // Other block-level closers, or a table-based mail collapses into one unreadable line.
      .replace(/<\/(div|tr|li|h[1-6]|table|blockquote)>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      // Entities. `&amp;` LAST, or `&amp;lt;` would decode all the way to `<`.
      .replace(/&nbsp;/gi, ' ')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#0?39;/g, "'")
      .replace(/&amp;/g, '&')
      /*
        A SECOND `&nbsp;` pass, and only for this entity. The real RFQ mail contains
        `&amp;nbsp;` — double-encoded by the sender — so one correct pass leaves a literal
        `&nbsp;` on screen, which reads as our bug rather than theirs.

        Decoding everything twice would be wrong: `&amp;lt;` must stay `&lt;`, not become `<`.
        A non-breaking space is the safe exception, because it is whitespace and never meaningful
        literal text in a business email.
      */
      .replace(/&nbsp;/gi, ' ')
      // See the header: source indentation, not content.
      .replace(/[^\S\n]+$/gm, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  )
}
